import { PropsWithChildren, useCallback, useRef } from 'react';
import {
  AppointmentProposalOptionModifier,
  AppointmentProposalOptionModifierCreate,
  AppointmentProposalOptionModifiersContext,
  AppointmentProposalOptionModifiersProvider,
  useAppointmentProposalOptionModifiers,
} from '../../../data-access/appointmentProposalOptionModifiers';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import { createOfflineUUID } from '../../rxdb/utils/rxDbOperations';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { useEntityReplication } from '../../rxdb/utils/useEntityReplication';

const buildOfflineModifier = (
  appointmentId: string,
  appointmentProposalId: string,
  appointmentProposalOptionId: string,
  create: AppointmentProposalOptionModifierCreate
): AppointmentProposalOptionModifier => ({
  appointmentProposalOptionModifierId: createOfflineUUID(),
  appointmentId,
  appointmentProposalId,
  appointmentProposalOptionId,
  entityType: 'APPOINTMENT_PROPOSAL_OPTION_MODIFIER',
  discountsAndFeesId: create.discountsAndFeesId,
  jobId: create.jobId,
  status: 'ACTIVE',
  externalIds: [],
  images: create.images ?? [],
  categoryIds: create.categoryIds ?? [],
  qualifyingPricebookCategoryIds: create.qualifyingPricebookCategoryIds ?? [],
  qualifyingPricebookIds: create.qualifyingPricebookIds ?? [],
  sortOrder: create.sortOrder ?? 0,
  roundingRule: create.roundingRule ?? { type: 'NEAREST', decimalPlaces: 2 },
  taxBasis: create.taxBasis ?? 'POST_TAX',
  amount: create.amount,
  amountType: create.amountType,
  description: create.description,
  limit: create.limit,
  modifierType: create.modifierType,
  taxable: create.taxable,
  displayName: create.displayName,
});

const OfflineAppointmentProposalOptionModifiersOverride: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const context = useAppointmentProposalOptionModifiers();
  const data = useOfflineDbData();
  const {
    upsertModifiers,
    selectProposalModifiers,
    setModifiersMutating,
    clearModifiersMutating,
  } = context;

  const lastProposalIdRef = useRef<string | null>(null);

  const syncModifiersInternal = useCallback(
    async (appointmentProposalId: string) => {
      const items = await data.find('appointment_proposal_option_modifiers', {
        selector: { appointmentProposalId },
      });
      upsertModifiers(items);
      return items;
    },
    [data, upsertModifiers]
  );

  const onSync = useCallback(async () => {
    if (lastProposalIdRef.current) {
      await syncModifiersInternal(lastProposalIdRef.current);
    }
  }, [syncModifiersInternal]);

  const { initError } = useEntityReplication(
    'appointment_proposal_option_modifiers',
    'airship-appointment-proposal-option-modifiers',
    { onSync, upsert: upsertModifiers }
  );

  const appendModifiers: typeof context.appendModifiers = useCallback(
    (modifiers) =>
      data.bulkUpsertSynced('appointment_proposal_option_modifiers', modifiers),
    [data]
  );

  const fetchAppointmentProposalModifiers: typeof context.fetchAppointmentProposalModifiers =
    useCallback(
      async (_appointmentId, appointmentProposalId) => {
        lastProposalIdRef.current = appointmentProposalId;
        selectProposalModifiers(appointmentProposalId);
        return syncModifiersInternal(appointmentProposalId);
      },
      [selectProposalModifiers, syncModifiersInternal]
    );

  const createAppointmentProposalOptionModifier: typeof context.createAppointmentProposalOptionModifier =
    useCallback(
      (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        create
      ) => {
        setModifiersMutating({ creatingIds: [create.discountsAndFeesId] });
        return data
          .insertSynced(
            'appointment_proposal_option_modifiers',
            buildOfflineModifier(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              create
            ),
            0
          )
          .then((result) => {
            upsertModifiers([result]);
            return result;
          })
          .catch((error) => {
            clearModifiersMutating({
              creatingIds: [create.discountsAndFeesId],
              error:
                'We were unable to add the discount or fee. Please try again.',
            });
            throw error;
          });
      },
      [data, setModifiersMutating, clearModifiersMutating, upsertModifiers]
    );

  const createManyAppointmentProposalOptionModifiers: typeof context.createManyAppointmentProposalOptionModifiers =
    useCallback(
      (appointmentId, appointmentProposalId, payloads) => {
        const creatingIds = payloads.map(
          ({ modifier }) => modifier.discountsAndFeesId
        );
        setModifiersMutating({ creatingIds });
        return data
          .bulkInsertSynced(
            'appointment_proposal_option_modifiers',
            payloads.map(({ appointmentProposalOptionId, modifier }) =>
              buildOfflineModifier(
                appointmentId,
                appointmentProposalId,
                appointmentProposalOptionId,
                modifier
              )
            ),
            0
          )
          .then((results) => {
            upsertModifiers(results);
            return results;
          })
          .catch((error) => {
            clearModifiersMutating({
              creatingIds,
              error:
                'We were unable to add the discounts or fees. Please try again.',
            });
            throw error;
          });
      },
      [data, setModifiersMutating, clearModifiersMutating, upsertModifiers]
    );

  const updateAppointmentProposalOptionModifier: typeof context.updateAppointmentProposalOptionModifier =
    useCallback(
      (
        _appointmentId,
        _appointmentProposalId,
        _appointmentProposalOptionId,
        appointmentProposalOptionModifierId,
        update
      ) => {
        setModifiersMutating({});
        return data
          .patchSynced(
            'appointment_proposal_option_modifiers',
            appointmentProposalOptionModifierId,
            { ...update, offlineSyncErrors: null },
            0
          )
          .then((result) => {
            upsertModifiers([result]);
            return result;
          })
          .catch((error) => {
            clearModifiersMutating({
              error:
                'We were unable to save the discount or fee. Please try again.',
            });
            throw error;
          });
      },
      [data, setModifiersMutating, clearModifiersMutating, upsertModifiers]
    );

  const deleteAppointmentProposalOptionModifier: typeof context.deleteAppointmentProposalOptionModifier =
    useCallback(
      (
        _appointmentId,
        _appointmentProposalId,
        _appointmentProposalOptionId,
        appointmentProposalOptionModifierId
      ) => {
        setModifiersMutating({});
        return data
          .softDeleteSynced(
            'appointment_proposal_option_modifiers',
            appointmentProposalOptionModifierId,
            0
          )
          .then((doc) => upsertModifiers([doc]))
          .catch((error) => {
            clearModifiersMutating({
              error:
                'We were unable to remove the discount or fee. Please try again.',
            });
            throw error;
          });
      },
      [data, setModifiersMutating, clearModifiersMutating, upsertModifiers]
    );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="appointment proposal option modifiers"
      />
    );
  }

  return (
    <AppointmentProposalOptionModifiersContext.Provider
      value={{
        ...context,
        appendModifiers,
        fetchAppointmentProposalModifiers,
        createAppointmentProposalOptionModifier,
        createManyAppointmentProposalOptionModifiers,
        updateAppointmentProposalOptionModifier,
        deleteAppointmentProposalOptionModifier,
      }}
    >
      {children}
    </AppointmentProposalOptionModifiersContext.Provider>
  );
};

export const OfflineAppointmentProposalOptionModifiersProvider: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <AppointmentProposalOptionModifiersProvider>
    <OfflineAppointmentProposalOptionModifiersOverride>
      {children}
    </OfflineAppointmentProposalOptionModifiersOverride>
  </AppointmentProposalOptionModifiersProvider>
);
